import PropTypes from "prop-types";
import { TbCheck, TbCopy, TbPlus, TbRefresh, TbTrash } from "react-icons/tb";
import CustomTextInput from "../../../ui/CustomTextInput/CustomTextInput";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../ui/CustomCard/CustomCard";
import CustomButton from "../../../ui/CustomButton/CustomButton";
import CustomInlineBanner from "../../../ui/CustomInlineBanner/CustomInlineBanner";
import GenerateApiKeyModal from "../modals/GenerateApiKeyModal";
import CustomLeaveConfirmationModal from "../../../ui/CustomLeaveConfirmationModal/CustomLeaveConfirmationModal";
import { SkeletonCard } from "../DeveloperPageSkeleton";
import FetchFeedback from "../../../ui/CustomFetchFeedback/CustomFetchFeedback";
import {
  notificationStyles,
  revokeBannerStyle,
  rotateBannerStyle,
  secretBannerStyle,
} from "../../styles/developerStyles";
import { useApiCredentials } from "../../hooks/useApiCredentials";

export default function ApiCredentialsCard({ apiEnabled, isDisabling }) {
  const {
    control,
    oauthClient,
    hasCredentials,
    isLoadingCredentials,
    isFetchError,
    onRetry,
    isCreating,
    isRotating,
    isBusy,
    modalOpen,
    showRotateConfirmation,
    oneTimeSecret,
    secretCopied,
    credentialNameValue,
    onOpenModal,
    onModalClose,
    onSubmit,
    onCopy,
    onCopySecret,
    onDismissSecret,
    onRotateClick,
    onCancelRotate,
    onConfirmRotate,
    showRevokeConfirmation,
    isRevoking,
    onRevokeClick,
    onCancelRevoke,
    onConfirmRevoke,
    navigationBlocker,
  } = useApiCredentials(apiEnabled);

  if (isLoadingCredentials) return <SkeletonCard bannerHeight={128} />;

  if (isFetchError)
    return (
      <Card className="bg-white border-[#e5e7eb] shadow-none">
        <CardContent>
          <FetchFeedback
            message="Failed to load credentials. Please try again."
            onRetry={onRetry}
            fullHeight={false}
            compact
          />
        </CardContent>
      </Card>
    );

  return (
    <>
      <CustomLeaveConfirmationModal
        opened={navigationBlocker.state === "blocked"}
        title="Your secret will be lost"
        message="You have an API secret that hasn't been copied yet. Once you leave, it's gone permanently you'll need to rotate your credentials to generate a new one."
        leaveLabel="Leave anyway"
        stayLabel="Stay and copy secret"
        leaveButtonColor="error"
        StayIcon={TbCopy}
        onStay={() => navigationBlocker.reset()}
        onLeave={() => navigationBlocker.proceed()}
      >
        <div className="mt-2 w-full rounded-lg bg-amber-50 border border-amber-200 px-4 py-3">
          <p className="text-xs font-medium text-amber-800">
            Copy the secret from the credentials card before navigating away.
          </p>
        </div>
      </CustomLeaveConfirmationModal>
      <GenerateApiKeyModal
        opened={modalOpen}
        onClose={onModalClose}
        control={control}
        onSubmit={onSubmit}
        isSubmitting={isCreating}
        isSubmitDisabled={!credentialNameValue?.trim()}
      />

      <Card className="bg-white border-[#e5e7eb] shadow-none">
        <CardHeader>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div>
              <CardTitle className="text-base font-semibold">
                API Credentials
              </CardTitle>
              <CardDescription className="mt-1 text-gray-500">
                Your API key and secret used to authenticate requests to the
                PayKit API
              </CardDescription>
            </div>
            <CustomButton
              leftSection={
                hasCredentials ? <TbRefresh size={15} /> : <TbPlus size={15} />
              }
              color="dark"
              radius="md"
              variant="filled"
              className="w-full sm:w-auto self-start sm:shrink-0"
              onClick={hasCredentials ? onRotateClick : onOpenModal}
              disabled={!!oneTimeSecret || isBusy || showRotateConfirmation || showRevokeConfirmation || isDisabling}
            >
              {hasCredentials ? "Rotate Key" : "Generate API Key"}
            </CustomButton>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-4">
            <div>
              <p className="mb-1.5 text-sm font-medium text-gray-700">
                Client ID
              </p>
              <div className="flex flex-col gap-2 min-[400px]:flex-row min-[400px]:items-center">
                <CustomTextInput
                  value={hasCredentials ? oauthClient.client_id : ""}
                  placeholder="sk_live_••••••••••••••••••••••"
                  readOnly
                  disabled={!hasCredentials || isBusy}
                  radius="md"
                  className="flex-1"
                  styles={{
                    input: {
                      fontFamily: "monospace",
                      fontSize: "0.8125rem",
                      textOverflow: "ellipsis",
                    },
                  }}
                />
                <CustomButton
                  variant="default"
                  leftSection={<TbCopy size={16} />}
                  disabled={!hasCredentials || isBusy}
                  onClick={onCopy}
                  className="w-full min-[370px]:w-auto min-[370px]:shrink-0"
                  radius="md"
                >
                  Copy
                </CustomButton>
              </div>
            </div>
            {showRotateConfirmation && (
              <CustomInlineBanner styles={rotateBannerStyle}>
                <div className="flex flex-col gap-3">
                  <p className="text-sm text-orange-800">
                    Rotating your API key will immediately invalidate the
                    current key. Any active integrations using the old key will
                    stop working until updated with the new credentials. This
                    action cannot be undone.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <CustomButton
                      variant="filled"
                      color="warning"
                      size="sm"
                      radius="md"
                      loading={isRotating}
                      onClick={onConfirmRotate}
                    >
                      Yes, Rotate Key
                    </CustomButton>
                    <CustomButton
                      variant="filled"
                      color="gray"
                      size="sm"
                      radius="md"
                      disabled={isRotating}
                      onClick={onCancelRotate}
                    >
                      Cancel
                    </CustomButton>
                  </div>
                </div>
              </CustomInlineBanner>
            )}
            {oneTimeSecret && (
              <CustomInlineBanner styles={secretBannerStyle}>
                <div className="flex flex-col gap-3">
                  <p className="text-sm font-medium text-amber-800">
                    Copy your secret now — it will not be shown again
                  </p>
                  <CustomTextInput
                    value={oneTimeSecret}
                    readOnly
                    radius="md"
                    styles={{
                      input: {
                        fontFamily: "monospace",
                        fontSize: "0.8125rem",
                      },
                    }}
                  />
                  <div className="flex flex-wrap items-center gap-2">
                    <CustomButton
                      variant="filled"
                      color="success"
                      size="sm"
                      radius="md"
                      leftSection={
                        secretCopied ? (
                          <TbCheck size={15} />
                        ) : (
                          <TbCopy size={15} />
                        )
                      }
                      onClick={onCopySecret}
                    >
                      {secretCopied
                        ? "Copied — store it securely"
                        : "Copy Secret"}
                    </CustomButton>
                    <CustomButton
                      variant="filled"
                      color="gray"
                      size="sm"
                      radius="md"
                      onClick={onDismissSecret}
                    >
                      I&apos;ve saved it securely
                    </CustomButton>
                  </div>
                </div>
              </CustomInlineBanner>
            )}
            {!hasCredentials && (
              <CustomInlineBanner styles={notificationStyles.info}>
                <p className="text-sm text-[var(--color-primary-600)]">
                  No API credentials have been generated yet. Click{" "}
                  <span className="font-medium">Generate API Key</span> above to
                  create your key and secret. Your secret will be shown only
                  once — store it securely.
                </p>
              </CustomInlineBanner>
            )}
            {hasCredentials && (
              <div className="border-t border-gray-100 pt-4 flex flex-col gap-3">
                {showRevokeConfirmation ? (
                  <CustomInlineBanner styles={revokeBannerStyle}>
                    <div className="flex flex-col gap-3">
                      <p className="text-sm text-red-800">
                        Revoking your API credentials will immediately
                        invalidate them. Any active integrations will stop
                        working until new credentials are generated. This action
                        cannot be undone.
                      </p>
                      <div className="flex flex-wrap items-center gap-2">
                        <CustomButton
                          color="error"
                          size="sm"
                          radius="md"
                          loading={isRevoking}
                          onClick={onConfirmRevoke}
                        >
                          Yes, Revoke
                        </CustomButton>
                        <CustomButton
                          variant="filled"
                          color="gray"
                          size="sm"
                          radius="md"
                          disabled={isRevoking}
                          onClick={onCancelRevoke}
                        >
                          Cancel
                        </CustomButton>
                      </div>
                    </div>
                  </CustomInlineBanner>
                ) : (
                  <CustomButton
                    variant="filled"
                    size="sm"
                    radius="md"
                    color="error"
                    leftSection={<TbTrash size={15} />}
                    disabled={!!oneTimeSecret || isBusy || showRotateConfirmation}
                    onClick={onRevokeClick}
                    className="self-start"
                  >
                    Revoke Credentials
                  </CustomButton>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </>
  );
}

ApiCredentialsCard.propTypes = {
  apiEnabled: PropTypes.bool.isRequired,
  isDisabling: PropTypes.bool.isRequired,
};
